import Vue from 'vue'
import Vuex from 'vuex'
import cookies from 'vue-js-cookie'
import AppHelper from "../helper";


Vue.use(Vuex)

const store = new Vuex.Store({
    state: {
        redirect: {},
        userInfo: {},
        companyInfo: {},
        structure: {},
        list: {},
        avatars: []
    },

    mutations: {
        setUserInfo(state, userInfo){
            Vue.set(state, 'userInfo', userInfo)
        },

        setCompanyInfo(state, companyInfo){
            Vue.set(state, 'companyInfo', companyInfo)
        },

        setStructure(state, structure){
            Vue.set(state, 'structure', structure)
        },

        setRedirect(state, redirect) {
            Vue.set(state, 'redirect', redirect)
        },

        setList(state, {module, list}){
            let value = {}
            
            // Initialize pagination state for modules that support it
            if (list.pagination) {
                value[module] = {
                    headers: list.headers,
                    entries: list.entries,
                    pagination: {
                        currentPage: list.pagination.currentPage || 1,
                        hasMoreData: list.entries && list.entries.length > 0,
                        isLoading: false,
                        isLoadingMore: false,
                        error: null
                    }
                }
            } else {
                // Maintain existing behavior for non-paginated modules
                value[module] = list
            }

            Vue.set(state, 'list', value)
        },

        appendList(state, {module, list}) {
            if (!state.list[module]) {
                return
            }

            // Append new entries to existing list
            const currentList = state.list[module]
            const newEntries = [...currentList.entries, ...list.entries]
            
            Vue.set(state.list[module], 'entries', newEntries)
            
            // Update pagination state
            if (currentList.pagination) {
                Vue.set(state.list[module].pagination, 'currentPage', list.pagination.currentPage || currentList.pagination.currentPage + 1)
                Vue.set(state.list[module].pagination, 'hasMoreData', list.entries && list.entries.length > 0)
                Vue.set(state.list[module].pagination, 'isLoadingMore', false)
                Vue.set(state.list[module].pagination, 'error', null)
            }
        },

        setPaginationLoading(state, {module, isLoadingMore}) {
            if (state.list[module] && state.list[module].pagination) {
                Vue.set(state.list[module].pagination, 'isLoadingMore', isLoadingMore)
            }
        },

        setPaginationError(state, {module, error}) {
            if (state.list[module] && state.list[module].pagination) {
                Vue.set(state.list[module].pagination, 'error', error)
                Vue.set(state.list[module].pagination, 'isLoadingMore', false)
            }
        },

        resetPagination(state, {module}) {
            if (state.list[module] && state.list[module].pagination) {
                Vue.set(state.list[module].pagination, 'currentPage', 1)
                Vue.set(state.list[module].pagination, 'hasMoreData', true)
                Vue.set(state.list[module].pagination, 'isLoadingMore', false)
                Vue.set(state.list[module].pagination, 'error', null)
            }
        },

        updateAvatar(state, payload) {
            Vue.set(state.avatars, payload.record, payload.avatar);
        }
    },

    actions: {
        async fetchStructure ({state, commit}) {
            if(!AppHelper.empty(state.structure)){
                return
            }

            let data = await Api.fetchStructure()
            if(!data.result || !AppHelper.empty(state.structure)){
                return
            }

            commit('setStructure', data.result)
        },

        async fetchList({state, commit}, {module, filters, sort, parentRecord, page}){
            let list = await Api.fetchList(module, parentRecord, filters, sort, page)
            if(list.result){
                // TODO: add this condition to fetch_list in HelpDesk module store
                if(module == 'HelpDesk'){
                    let ignoreUser = true
                    list.result.entries.forEach((entry, index) => {
                        entry['currentUser'] = cookies.get('crmid')
                        if(entry.contact_id.value !== entry.currentUser){
                            ignoreUser = false
                        }
                    })

                    if(ignoreUser){
                        list.result.entries.forEach((entry, index) => {
                            entry['ignoreUser'] = true
                        })
                    }
                }

                commit('setList', {module: module, list: list.result})
            }
        },

        async fetchAvatar(context, payload) {
            let response = await Api.fetchAvatar(payload.record);
            if (response.success && response.result.length > 50) {
                context.commit('updateAvatar', { record: payload.record, avatar: response.result });
            }
        },

        async fetchNextPage({state, commit}, {module, filters, sort, parentRecord}) {
            const currentList = state.list[module]
            if (!currentList || !currentList.pagination || !currentList.pagination.hasMoreData || currentList.pagination.isLoadingMore) {
                return
            }

            const nextPage = currentList.pagination.currentPage + 1
            
            try {
                commit('setPaginationLoading', {module, isLoadingMore: true})
                
                let list = await Api.fetchList(module, parentRecord, filters, sort, nextPage)
                
                // Check if the response indicates success
                if (list.success === false) {
                    // Backend returned an error response
                    const errorMessage = list.error && list.error.message 
                        ? list.error.message 
                        : 'Backend error occurred'
                    
                    commit('setPaginationError', {module, error: errorMessage})
                    
                    // Throw error to trigger component retry mechanism
                    throw new Error(errorMessage)
                }
                
                if (list.result) {
                    // Apply HelpDesk specific logic if needed (maintain existing behavior)
                    if (module === 'HelpDesk') {
                        let ignoreUser = true
                        list.result.entries.forEach((entry, index) => {
                            entry['currentUser'] = cookies.get('crmid')
                            if (entry.contact_id.value !== entry.currentUser) {
                                ignoreUser = false
                            }
                        })

                        if (ignoreUser) {
                            list.result.entries.forEach((entry, index) => {
                                entry['ignoreUser'] = true
                            })
                        }
                    }

                    commit('appendList', {module, list: list.result})
                } else {
                    commit('setPaginationError', {module, error: 'Failed to load more data'})
                    throw new Error('Failed to load more data')
                }
            } catch (error) {
                // Handle both network errors and backend errors
                const errorMessage = error.message || 'Network error'
                commit('setPaginationError', {module, error: errorMessage})
                
                // Re-throw to ensure component retry mechanism is triggered
                throw error
            }
        },

        async resetPagination({commit}, {module}) {
            commit('resetPagination', {module})
        }
    },

    getters: {
        getStructure(state) {
            return state.structure
        },

        getMenuItems (state) {
            let structure = state.structure

            let menuItems = [];

            if(AppHelper.empty(structure)){
                return []
            }

            let modules = structure.modules

            for(let moduleName in modules){
                let structure = modules[moduleName]
                if(!structure.viewable || moduleName == 'ModComments'){
                    continue
                }

                menuItems.push({
                    'name': structure.name,
                    'label': structure.label,
                    'icon': AppHelper.getModuleIcon(structure.name),
                    'link': { name: 'Index', params: {view:'Index', module: structure.name}}
                });
            }

            return menuItems;
        },

        getQuickCreateFields: state => module => {
            let structure = state.structure
            let quickCreateFields = {}

            if(AppHelper.empty(structure)){
                return []
            }

            let blocks = structure.modules[module]['blocks']

            for(let key in blocks){
                let fields = blocks[key].fields

                for(let key in fields){
                    let field = Object.assign({}, fields[key])
                    //delete field.value
                    if(field.editable){
                        quickCreateFields[field.name] = field
                    }

                    if(field.type === 'picklist' || field.type === 'multipicklist'){
                        if(field.picklistvalues){
                            field.picklistvalues = AppHelper.convertFieldValuesToPickListOptions(field.name, field.picklistvalues)
                        }
                    }
                }
            }

            return quickCreateFields
        },

        getDetailBlocks: state => params => {
            let structure = state.structure
            let details = params.details
            let detailBlocks = []

            if(AppHelper.empty(structure)){
                return []
            }

            let blocks = structure.modules[params.module]['blocks']

            for(let key in blocks){
                let block = blocks[key]
                let fields = block.fields
                let detailFields = []

                for(key in fields){
                    let field = $.extend({}, fields[key])
                    let detail = details[field.name]

                    if(AppHelper.empty(detail) || AppHelper.empty(detail.displayValue)){
                        continue
                    }

                    field.displayValue = detail.displayValue
                    field.value = detail.value

                    detailFields.push(field)
                }

                if(AppHelper.empty(detailFields)){
                    continue
                }

                detailBlocks.push({'label': block.label, 'fields': detailFields})
            }

            return detailBlocks
        },

        getList: state => module => {
            return state.list[module]
        },

        getFields: state => module => {
            let structure = state.structure
            let fields = []

            if(AppHelper.empty(structure)){
                return fields
            }

            let blocks = structure.modules[module]['blocks']

            for(let key in blocks){
                fields = Object.assign(fields, blocks[key].fields)
            }

            return fields

        },

        getCustomFieldLabel: (state, getters) => (module, fieldKey) => {
            let fields = getters.getFields(module);
            return fields[fieldKey]['label'];
        },

        getAvatar: state => record => {
            return state.avatars[record];
        },

        getPortalLabel(state){
            if(!AppHelper.empty(state.structure) && state.structure.access.client_portal === true){
                return 'LBL_CLIENT_PORTAL'
            }
            else{
                return 'LBL_GDPR_PORTAL'
            }
        }
    }
});

export default store
